import { useCallback, useEffect, useState } from "react";
import { Bot, Clock, Inbox } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../../../connect/api";
import { Avatar, Empty, Header, LoadMore, ago, dayLabel, usePaged } from "../ui";

/** CEO inbox of every real-staff conversation; filter=puppet shows only DM-profile chats. */
export const AdminInbox = ({ mode = "puppet" }) => {
  const [filter, setFilter] = useState(mode === "puppet" ? "pending" : "all");
  const fetcher = useCallback((p) => api.get("/admin/conversations", { params: { filter: filter === "pending" ? "pending" : mode === "puppet" ? "puppet" : "all", page: p, limit: 30 } }).then((r) => r.data), [filter, mode]);
  const list = usePaged(fetcher, [filter, mode]);
  const reload = list.reload;
  useEffect(() => { const t = setInterval(reload, 10000); return () => clearInterval(t); }, [reload]);

  const puppet = mode === "puppet";
  return (
    <div className="h-full flex flex-col">
      <Header title={puppet ? "Staff-profile inbox" : "All conversations"} sub={puppet ? "Messages sent to DM profiles — you reply as them" : "Every conversation involving staff"} back="/connect/admin" />
      <div className="px-3 py-2 flex gap-1.5 border-b border-white/5 shrink-0">
        <button onClick={() => setFilter("pending")} className={`nxc-chip ${filter === "pending" ? "nxc-chip-active" : ""}`} data-testid="inbox-filter-pending">Awaiting reply</button>
        <button onClick={() => setFilter("all")} className={`nxc-chip ${filter === "all" ? "nxc-chip-active" : ""}`} data-testid="inbox-filter-all">{puppet ? "All profile chats" : "All"}</button>
      </div>
      <div className="flex-1 overflow-y-auto" onScroll={list.onScroll} data-testid="admin-inbox-list">
        {!list.loading && list.items.length === 0 && <Empty icon={Inbox} title={filter === "pending" ? "Nothing awaiting your reply" : "No conversations yet"} />}
        <ul className="divide-y divide-white/5">
          {list.items.map((c) => {
            const real = c.participants.find((p) => !p.is_dummy) || c.participants[0];
            const other = c.participants.find((p) => p.id !== real.id) || c.participants[1];
            return (
              <li key={c.id}>
                <Link to={`/connect/admin/inbox/${c.id}`} className={`flex items-start gap-3 px-4 py-3 hover:bg-white/[0.03] transition-colors ${c.ceo_unread ? "bg-amber-500/[0.05]" : ""}`} data-testid={`inbox-item-${c.id}`}>
                  <Avatar name={real.name} src={real.avatar} size={42} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <div className={`truncate text-sm ${c.ceo_unread ? "font-semibold text-white" : "text-slate-200"}`}>{real.name} <span className="text-slate-500">→</span> {other?.name}</div>
                      <div className="text-[11px] text-slate-500 shrink-0">{ago(c.last_at)}</div>
                    </div>
                    {c.dummy && <div className="text-[11px] text-amber-300/90 font-mono truncate">as {c.dummy.name} — {c.dummy.department} — {c.dummy.office}</div>}
                    <div className="text-sm text-slate-400 truncate mt-0.5">{c.last_text}</div>
                    <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-500">
                      {c.auto_reply_due_at && <span className="inline-flex items-center gap-1 text-amber-400"><Clock size={11} /> auto-reply {dayLabel(c.auto_reply_due_at)}</span>}
                      {c.auto_replied && <span className="inline-flex items-center gap-1"><Bot size={11} /> auto-replied</span>}
                    </div>
                  </div>
                  {c.ceo_unread && <span className="w-2.5 h-2.5 rounded-full bg-amber-400 mt-2 shrink-0" />}
                </Link>
              </li>
            );
          })}
        </ul>
        <LoadMore hasMore={list.hasMore} loading={list.loading && list.items.length > 0} onClick={list.loadMore} />
      </div>
    </div>
  );
};
